import { Opportunity, type Organization } from "@entities";
import { Box, Button, Checkbox, Chip, ListItemText, MenuItem, Select, TextField } from "@mui/material";
import { createPhoneNumberFilter, type HyperTableFilter } from "@shared/frontend/hyper-table";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import { useState } from "react";
import { FormGoogle } from "../../../components/form-components/form-google-autocomplete/FormGoogle";
import { BasicButton } from "../../../components/generic-components/button/Button";
import { TeamsSelect } from "../../../components/teams-select";
import { opportunityAgeRangeFilter } from "../opportunity-age-range-filter";

const getFilters: (
  currOrganization?: Organization,
) => HyperTableFilter<Opportunity>[] = (currOrganization) => {
  return [
      { fieldName: "title", id: "title" },
      { fieldName: "location", id: "location" },
      { fieldName: "comments", id: "comments" },
      opportunityAgeRangeFilter,
      {
        fieldName: "associationRequested.user.name",
        id: "associationRequestedUsername",
      },
      createPhoneNumberFilter(Opportunity, {
        fieldName: "associationRequested.user.phoneNumber",
        caption: "טלפון מבקש סיוע",
        id: "associationRequestedPhoneNumber",
      }),
      {
        id: "teams",
        icon: "literal",
        caption: "פרויקטים",
        renderFilter: ({ onSave, meta }) => {
          const { teams } = meta;
          return (
            <TeamsSelect
              id="opportunities-table"
              value={teams as string[]}
              onChange={(teams) =>
                onSave({
                  value: teams.length
                    ? Opportunity.hyperFilter({
                      teams: {
                        $some: {
                          teamId: teams as string[],
                        },
                      },
                    })
                    : {},
                  meta: { teams },
                })
              }
            />
          );
        },
      },
      {
        id: "categories",
        icon: "literal",
        caption: "קטגוריה",
        renderFilter: ({ onSave, meta }) => {
          const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
            (meta?.selectedCategoryIds as string[]) || [],
          );
          const categories = (currOrganization?.categories || [])
            .slice()
            .sort(compareBySortOrder);
          const catalogIds = new Set(categories.map((category) => category.id));
          const knownCategoryIds = selectedCategoryIds.filter((id) =>
            catalogIds.has(id),
          );

          const handleChange = (event: any) => {
            const { target: { value } } = event;
            const selectedIds = typeof value === "string" ? value.split(",") : value;
            setSelectedCategoryIds(
              selectedIds.filter((id: string) => catalogIds.has(id)),
            );
          };

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", paddingInline: "0.5rem" }}>
              <Select
                multiple
                value={knownCategoryIds}
                onChange={handleChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((id) => {
                      const name = categories.find((c) => c.id === id)?.name?.trim();
                      if (!name) return null;
                      return <Chip key={id} label={name} size="small" />;
                    })}
                  </Box>
                )}
                MenuProps={{ disableScrollLock: true, PaperProps: { sx: { borderRadius: "20px" } } }}
                fullWidth
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    <Checkbox checked={knownCategoryIds.includes(category.id)} />
                    <ListItemText primary={category.name} />
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value: knownCategoryIds.length
                      ? Opportunity.hyperFilter({
                          categories: { $some: { categoryId: { $in: knownCategoryIds } } },
                        })
                      : {},
                    meta: { selectedCategoryIds: knownCategoryIds },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          );
        },
      },
      {
        id: "isAddressInRadiusFromPoint",
        icon: "string",
        caption: "מרחק מכתובת",
        renderFilter: ({ onSave, meta }) => {
          const filterObj = {
            location: meta?.location || "",
            distanceKm: meta?.distanceKm || 0,
            latitude: meta?.latitude || 0,
            longitude: meta?.longitude || 0,
          };

          const [state, setState] = useState(filterObj);

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                paddingInline: "0.5rem",
              }}
            >
              <FormGoogle
                label="מיקום"
                placeValue={state.location as string}
                dir="rtl"
                onLocationChange={(location) => {
                  setState({
                    ...state,
                    location: location.locationName,
                    latitude: location.latLng.lat,
                    longitude: location.latLng.lng,
                  });
                }}
              />
              <TextField
                type="number"
                label="מרחק בקילומטרים"
                value={state.distanceKm}
                onChange={(e) =>
                  setState({
                    ...state,
                    distanceKm: Number(e.target.value) || "",
                  })
                }
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <BasicButton
                  style={{
                    borderRadius: "10px",
                    backgroundColor: "#05204c",
                    padding: "0.3rem 1rem",
                    fontWeight: 400,
                    fontSize: "small",
                  }}
                  text="שמירה"
                  onClick={() => {
                    onSave({
                      value: Opportunity.isAddressInRadiusFromPoint({
                        radius: {
                          distance: Number(state.distanceKm),
                          measure: "km",
                        },
                        lat: state.latitude as number,
                        lng: state.longitude as number,
                      }),
                      meta: state,
                    });
                  }}
                />
              </div>
            </div>
          );
        },
      },
    ];
};

export default getFilters;
